import { Injectable, inject } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';
import { Guid, once } from '@web/utils';
import { AdminMultimediaApiService } from '../api/services/admin-multimedia-api.service';
import { AdminMultimediaCollectionItem } from '../models/admin-multimedia-collection-item';
import { AdminMultimediaChunk } from '../models/admin-multimedia-chunk';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
} from '@web/shared/ui';
import { AdminMultimediaListFilter } from '../models/admin-multimedia-list-filter';
import { AdminMultimediaGridChunkLazyLoad } from '../models/admin-multimedia-grid-chunk-lazy-load';
import { AdminMultimediaDetails } from '../models/admin-multimedia-details';
import { AdminMultimediaDetailsForm } from '../models/admin-multimedia-details-form';
import {
  CollectionFilterRequestBuilder,
  CollectionRequestBuilder,
} from '@web/common';
import { AdminMultimediaGridFilter } from '../models/admin-multimedia-grid-filter';
import { MultimediaUpdateRequest } from '../api/requests/multimedia-update.request';

@Injectable({
  providedIn: 'root',
})
export class AdminMultimediaDataSourceService {
  private readonly multimediaApi = inject(AdminMultimediaApiService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  public update(id: Guid, form: AdminMultimediaDetailsForm): Observable<void> {
    return this.multimediaApi.update(
      id,
      new MultimediaUpdateRequest(form.title, form.alt, form.description),
    );
  }

  public upload(file: File): Observable<AdminMultimediaDetails> {
    return this.multimediaApi.upload(file).pipe(
      switchMap((response) => this.multimediaApi.fetchDetailsById(response.id)),
      map(
        (response) =>
          new AdminMultimediaDetails({
            id: response.id,
            filename: response.title,
            thumbnailPreviewUrl: response.miniThumbUrl(),
            previewUrl: response.largeThumbUrl(),
            extension: response.extension,
            mimeType: response.mimeType,
            size: response.size,
            createdAt: response.createdAt,
            downloadUrl: response.downloadUrl,
            alt: response.alt,
            type: response.type,
            width: response.dimensions?.width,
            height: response.dimensions?.height,
            description: response.description,
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
          }),
      ),
    );
  }

  public confirmAndRemoveById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybrany plik?')
          .accept(() =>
            this.multimediaApi.remove(id).pipe(once()).subscribe(subscriber),
          )
          .build(),
      );
    });
  }

  public fetchDetailsById(
    id: Guid,
    cacheable = false,
  ): Observable<AdminMultimediaDetails> {
    return this.multimediaApi.fetchDetailsById(id, cacheable).pipe(
      map(
        (response) =>
          new AdminMultimediaDetails({
            alt: response.alt,
            canBeEdited: response.contextOperations.canBeEdited,
            canBeRemoved: response.contextOperations.canBeDeleted,
            createdAt: response.createdAt,
            description: response.description,
            extension: response.extension,
            filename: response.title,
            height: response.dimensions?.height,
            downloadUrl: response.downloadUrl,
            id: response.id,
            mimeType: response.mimeType,
            previewUrl: response.largeThumbUrl(),
            size: response.size,
            thumbnailPreviewUrl: response.miniThumbUrl(),
            type: response.type,
            width: response.dimensions?.width,
          }),
      ),
    );
  }

  public fetchDetailsFormById(
    id: Guid,
  ): Observable<AdminMultimediaDetailsForm> {
    return this.multimediaApi
      .fetchDetailsById(id)
      .pipe(
        map(
          (response) =>
            new AdminMultimediaDetailsForm(
              response.title,
              response.alternativeText,
              response.description,
            ),
        ),
      );
  }

  public fetchGridChunk(
    lazyLoad: AdminMultimediaGridChunkLazyLoad,
  ): Observable<AdminMultimediaChunk> {
    return this.multimediaApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.part)
          .pageSize(lazyLoad.size)
          .filters(
            (
              builder: CollectionFilterRequestBuilder<AdminMultimediaGridFilter>,
            ) =>
              builder
                .appendText((builder) =>
                  builder //
                    .inFor('type')
                    .value(lazyLoad.filter?.type)
                    .build(),
                )
                .appendText((builder) =>
                  builder //
                    .caseIntensiveLikeFor('title')
                    .value(lazyLoad.filter?.title)
                    .build(),
                )
                .appendDate((builder) =>
                  builder
                    .rangeFor('createdAt')
                    .value(lazyLoad.filter?.createdAt)
                    .build(),
                )
                .build(),
          )

          .build(),
      )
      .pipe(
        map((response) => {
          const { items, total } = response;
          const chunk = items.map(
            (item) =>
              new AdminMultimediaCollectionItem(
                item.id,
                item.title,
                item.createdAt,
                item.alt,
                item.miniThumbUrl,
                item.extension,
                item.size,
                item.type,
                item.contextOperations.canBeEdited,
                item.contextOperations.canBeDeleted,
              ),
          );

          return new AdminMultimediaChunk(chunk, total);
        }),
      );
  }

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminMultimediaListFilter>,
  ): Observable<TableDataPayload<AdminMultimediaCollectionItem>> {
    return this.multimediaApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('title').build(),
              )
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload<AdminMultimediaCollectionItem>(
              response.items.map(
                (item) =>
                  new AdminMultimediaCollectionItem(
                    item.id,
                    item.title,
                    item.createdAt,
                    item.alt,
                    item.miniThumbUrl,
                    item.extension,
                    item.size,
                    item.type,
                    item.contextOperations.canBeEdited,
                    item.contextOperations.canBeDeleted,
                  ),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }
}
